import angular from 'angular';
import { angularComponent } from '@/legacy/helpers';
import camelcaseKeys from 'camelcase-keys';

const PROMOTION_MESSAGE_STYLES_MAP = {
  'backgroundColor':     '--l12-notice-background',
  'textColor':           '--l12-notice-text-color',
  'linkColor':           '--l12-notice-link-color',
  'linkBackgroundColor': '--l12-notice-link-background',
};

const MAIN_NOTICE_ROOT_CLASS = 'l12-main-notice-show';

interface PromotionMessage {
  id: number;
  backgroundColor: string;
  linkBackgroundColor: string;
  linkColor: string;
  text: string;
  textColor: string;
}

interface PromotionPopup {
  id: number;
  backgroundColor: string;
  textColor: string;
  linkColor: string;
  linkBackgroundColor: string;
  title: string|null;
  text: string;
  image: string|null;
  buttonTitle: string|null;
  buttonUrl: string|null;
}

class MainNoticeComponent {
  static $inject = ['$scope', '$rootScope', '$http', '$timeout'];

  public message: PromotionMessage|null = null;
  public popup: PromotionPopup|null = null;
  public fetchingMessage: boolean = false;
  // ID сообщений/попапов, закрытых в текущей сессии — чтобы не показывать их повторно,
  // пока сервер ещё возвращает их (например, если DELETE не успел отработать)
  private dismissedIds: Set<number> = new Set();
  public styleVars: Record<string, string> = {};
  public popupStyleVars: Record<string, string> = {};

  public expanded: boolean = false;

  constructor(
    private $scope: angular.IScope,
    private $rootScope: angular.IRootScopeService,
    private $http: angular.IHttpService,
    private $timeout: angular.ITimeoutService,
  ) {}

  public $onInit(): void {
    this.fetchMessage();
    this.startUpdatingWhenRouting();
  }

  public async fetchMessage() {
    if (this.fetchingMessage) {
      return; // Обновление уже выполняется
    }

    this.fetchingMessage = true;

    try {
      const { data } = await this.$http.get('/api/promotion/message');
      const payload = (data || {}) as { cabinet?: any; cabinet_popup?: any };

      // await переводит продолжение в нативный микротаск вне digest-цикла Angular,
      // поэтому изменения this.message/this.popup не отрисуются до следующего digest.
      // $timeout оборачивает применение данных в $apply и запускает digest принудительно.
      this.$timeout(() => {
        this.applyCabinetMessage(payload.cabinet);
        this.applyCabinetPopup(payload.cabinet_popup);
      });
    } finally {
      this.fetchingMessage = false;
    }
  }

  public async dismissMessage() {
    if (this.expanded) {
      this.expanded = false;
      return;
    }

    const removeMessage = this.message || null;

    if (!removeMessage) {
      throw new Error('Нет нотификаций для удаления');
    }

    if (!removeMessage.id) {
      throw new Error('Отстутствует ID у удаляемой нотификации');
    }

    // Удаляем из интерфейса сразу, чтобы был мгновенный отклик!
    // А так-же не даем возможности пользователю нажать еще раз,
    // до того момента, как выполнится запрос
    this.dismissedIds.add(removeMessage.id);
    this.setMessage(null);

    await this.$http.delete(`/api/promotion/message/${removeMessage.id}`);

    // Повторно запросим сообщение
    // TODO: Это кастыль и от такого необходимо избавляться
    this.fetchMessage(); // async
  }

  // Клик вне попапа просто закрывает его без запроса на сервер.
  // В отличие от dismissPopup, попап не помечается прочитанным и появится снова.
  public closePopup() {
    this.setPopup(null);
  }

  public async dismissPopup() {
    const removePopup = this.popup;
    if (!removePopup || !removePopup.id) {
      this.setPopup(null);
      return;
    }

    this.dismissedIds.add(removePopup.id);
    this.setPopup(null);

    await this.$http.delete(`/api/promotion/message/${removePopup.id}`);
  }

  public toggleExpanded(ev?: any) {
    if (ev && ev?.currentTarget !== ev?.target) {
      // Чтобы клик по внутренним ссылкам не триггерил блок
      return;
    }

    this.expanded = !this.expanded;
  }

  private applyCabinetMessage(raw: any) {
    if (!raw || !raw.id || this.dismissedIds.has(raw.id)) {
      this.setMessage(null);
      return;
    }
    if (this.message && this.message.id === raw.id) {
      // Сообщение с таким id уже отображается — обновлять DOM не нужно
      return;
    }
    this.setMessage(camelcaseKeys(raw) as PromotionMessage);
  }

  private applyCabinetPopup(raw: any) {
    if (!raw || !raw.id || this.dismissedIds.has(raw.id)) {
      this.setPopup(null);
      return;
    }
    if (this.popup && this.popup.id === raw.id) {
      return;
    }
    this.setPopup(camelcaseKeys(raw) as PromotionPopup);
  }

  private setMessage(m: PromotionMessage|null) {
    if (m) {
      this.message = m;
      this.styleVars = this.buildPromotionStyleVars(m);
      document.documentElement.classList.add(MAIN_NOTICE_ROOT_CLASS);
    } else {
      this.message = null;
      this.styleVars = null;
      document.documentElement.classList.remove(MAIN_NOTICE_ROOT_CLASS);
    }
  }

  private setPopup(p: PromotionPopup|null) {
    this.popup = p;
    this.popupStyleVars = p ? this.buildPromotionStyleVars(p as any) : {};
  }

  private buildPromotionStyleVars(m: PromotionMessage|null): Record<string, string> {
    if (typeof m !== 'object' || m === null) {
      return {};
    }

    let styleVars = {};

    for (var k in PROMOTION_MESSAGE_STYLES_MAP) {
      if (m[k]) {
        const propName = PROMOTION_MESSAGE_STYLES_MAP[k];
        styleVars[propName] = m[k];
      }
    }

    return styleVars;
  }

  /**
   * Привязывает событие обновления страниц в роутере и при переходе
   * на корневую страницу запускает обновление сообщения
   *
   * TODO: Переделать бэк и фронт под нормальную логику со списком сообщений,
   * которые будут отображаться по кругу и не грузить сообщения при каждом переходе
   */
  private startUpdatingWhenRouting() {
    let isFirstRouted = false;

    const routeChangeUnsubscribe = this.$rootScope.$on('$stateChangeSuccess', (event, toState, toParams, fromState, fromParams) => {
      if (!isFirstRouted) {
        // При первой маршрутизации не загружаем, чтобы не повторно
        // не вызывать загрузку сообщений при запуске приложения
        isFirstRouted = true;
        return;
      }

      const isRootState = toState.name.indexOf('.') === -1;

      if (isRootState) {
        this.fetchMessage();
      }
    });

    this.$scope.$on('$destroy', () => routeChangeUnsubscribe());
  }
}

angularComponent('mainNotice', {
  template: require('html-loader!./main-notice.component.html')['default'],
  controller: MainNoticeComponent,
});
